type Row = Record<string, unknown>;

function blockKey(row: Row): string {
  return `${String(row.metric_name)}|${String(row.fiscal_year_name)}`;
}

/**
 * Places each algorithmic row directly after the last grid row sharing its
 * KPI and fiscal year, so it reads against the planner's row the way the
 * SPEC's result-rows mock shows. The default sort mode ("None") keeps
 * rowData order, so position has to be built here. Algo rows with no
 * matching block append at the end; load order is kept within a block.
 */
export function interleaveAlgoRows<T extends Row>(
  baseRows: T[],
  algoRows: T[]
): T[] {
  if (algoRows.length === 0) return baseRows;
  const lastIndexOfBlock = new Map<string, number>();
  baseRows.forEach((row, index) => lastIndexOfBlock.set(blockKey(row), index));
  const byBlock = new Map<string, T[]>();
  const orphans: T[] = [];
  for (const row of algoRows) {
    const key = blockKey(row);
    if (lastIndexOfBlock.has(key)) {
      byBlock.set(key, [...(byBlock.get(key) ?? []), row]);
    } else {
      orphans.push(row);
    }
  }
  const merged: T[] = [];
  baseRows.forEach((row, index) => {
    merged.push(row);
    const key = blockKey(row);
    if (lastIndexOfBlock.get(key) === index) {
      merged.push(...(byBlock.get(key) ?? []));
    }
  });
  return [...merged, ...orphans];
}
